import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Instagram, Twitter, Youtube, Facebook } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const STORES = [
  {
    city: 'New York',
    address: '212 Elizabeth St, NY 10012',
    hours: 'Mon–Sat 11–7 · Sun 12–6',
    phone: '+1 (212) 555-0114',
    mapQuery: '212+Elizabeth+St+New+York',
  },
  {
    city: 'Kyoto',
    address: '384 Nishinokyō Nishinocho, Nakagyō',
    hours: 'Tue–Sun 11–7',
    phone: '+81 (0)75 555 0142',
    mapQuery: 'Nishinokyō+Nishinocho+Nakagyō+Kyoto',
  },
  {
    city: 'Copenhagen',
    address: 'Bredgade 25, 1260 København K',
    hours: 'Mon–Sat 10–6',
    phone: '+45 33 555 014',
    mapQuery: 'Bredgade+25+Copenhagen',
  },
];

const SOCIALS = [
  { label: 'Instagram', Icon: Instagram, href: 'https://instagram.com', handle: '@evermark' },
  { label: 'Twitter',   Icon: Twitter,   href: 'https://twitter.com',   handle: '@evermark' },
  { label: 'YouTube',   Icon: Youtube,   href: 'https://youtube.com',   handle: 'Evermark' },
  { label: 'Facebook',  Icon: Facebook,  href: 'https://facebook.com',  handle: 'Evermark' },
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [activeStore, setActiveStore] = useState(0);

  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    e.currentTarget.reset();
  };

  const store = STORES[activeStore];

  return (
    <div data-testid="page-contact" className="container py-16 md:py-24">
      <SEO
        title="Contact"
        description="Reach the studio for orders, care, private appointments, or press. Ateliers in New York, Kyoto, and Copenhagen."
        path="/contact"
        breadcrumb={[{ to: '/', label: 'Home' }, { label: 'Contact' }]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Evermark',
          contactPoint: { '@type': 'ContactPoint', email: 'hello@evermark.com', contactType: 'customer service' },
        }}
      />
      <Breadcrumb items={[{ to: '/', label: 'Home' }, { label: 'Contact' }]} className="mb-8" />

      <p className="overline text-muted-foreground">Correspondence</p>
      <h1 className="mt-3 font-display text-6xl md:text-8xl leading-[0.9]">Say hello.</h1>
      <p className="mt-6 max-w-xl text-muted-foreground text-lg leading-relaxed">
        For orders, care, private appointments, or press — write to us. We read every note.
      </p>

      <div className="mt-16 grid md:grid-cols-2 gap-16">
        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
          <div>
            <label className="overline text-muted-foreground" htmlFor="c-name">Name</label>
            <input id="c-name" required data-testid="contact-name" className="mt-2 w-full bg-transparent border-b border-foreground py-3 focus:outline-none placeholder:text-muted-foreground/60" placeholder="Your name" />
          </div>
          <div>
            <label className="overline text-muted-foreground" htmlFor="c-email">Email</label>
            <input id="c-email" type="email" required data-testid="contact-email" className="mt-2 w-full bg-transparent border-b border-foreground py-3 focus:outline-none placeholder:text-muted-foreground/60" placeholder="you@domain.com" />
          </div>
          <div>
            <label className="overline text-muted-foreground" htmlFor="c-topic">Topic</label>
            <select id="c-topic" data-testid="contact-topic" className="mt-2 w-full bg-transparent border-b border-foreground py-3 focus:outline-none">
              <option className="bg-background">Orders & Shipping</option>
              <option className="bg-background">Returns</option>
              <option className="bg-background">Sizing & Fit</option>
              <option className="bg-background">Wholesale</option>
              <option className="bg-background">Press</option>
              <option className="bg-background">Private appointment</option>
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground" htmlFor="c-msg">Message</label>
            <textarea id="c-msg" required rows={5} data-testid="contact-message" className="mt-2 w-full bg-transparent border-b border-foreground py-3 focus:outline-none placeholder:text-muted-foreground/60 resize-none" placeholder="Tell us more." />
          </div>
          <Button type="submit" size="lg" data-testid="contact-submit">Send Message</Button>
          {sent && (
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="overline text-foreground" data-testid="contact-success">
              — Received. We'll respond within one business day.
            </motion.p>
          )}
        </form>

        {/* Details column */}
        <div className="space-y-12">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Mail className="h-4 w-4 mt-1 shrink-0" />
              <div>
                <p className="overline text-muted-foreground">Write</p>
                <a href="mailto:hello@evermark.com" data-testid="contact-email-link" className="mt-1 block text-lg link-underline">hello@evermark.com</a>
                <a href="mailto:press@evermark.com" data-testid="contact-press-link" className="mt-1 block text-sm link-underline text-muted-foreground">press@evermark.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="h-4 w-4 mt-1 shrink-0" />
              <div>
                <p className="overline text-muted-foreground">Call</p>
                <p className="mt-1 text-lg tabular">{store.phone}</p>
                <p className="mt-1 overline text-muted-foreground">Mon–Fri 9–6 ET</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="h-4 w-4 mt-1 shrink-0" />
              <div>
                <p className="overline text-muted-foreground">Head office</p>
                <p className="mt-1">{store.address}</p>
                <p className="mt-1 overline text-muted-foreground">{store.hours}</p>
              </div>
            </div>
          </div>

          {/* Socials */}
          <div>
            <p className="overline text-muted-foreground mb-4">Follow along</p>
            <ul className="grid grid-cols-2 gap-3" data-testid="contact-socials">
              {SOCIALS.map(({ label, Icon, href, handle }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    className="flex items-center gap-3 border border-border px-4 py-3 hover:border-foreground transition-colors duration-500"
                  >
                    <Icon className="h-4 w-4" />
                    <span>
                      <span className="block text-sm">{label}</span>
                      <span className="block overline text-muted-foreground">{handle}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Ateliers + map */}
      <section className="mt-24 md:mt-32" data-testid="contact-ateliers">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <p className="overline text-muted-foreground">Visit us</p>
            <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.95]">Three ateliers, one house.</h2>
          </div>
          <p className="md:col-span-5 text-muted-foreground md:justify-self-end max-w-sm">
            Each atelier is small — a few racks, considered light. Book a private appointment for a slower visit.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-8">
          {STORES.map((s, i) => (
            <button
              key={s.city}
              onClick={() => setActiveStore(i)}
              data-testid={`store-tab-${s.city.toLowerCase()}`}
              className={`text-left p-8 border transition-colors duration-500 ${activeStore === i ? 'border-foreground bg-foreground/[0.02]' : 'border-border hover:border-foreground'}`}
            >
              <p className="overline tabular text-muted-foreground">0{i + 1}</p>
              <h3 className="mt-3 font-display text-3xl">{s.city}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{s.address}</p>
              <p className="mt-1 overline text-muted-foreground">{s.hours}</p>
            </button>
          ))}
        </div>

        {/* Google Maps embed */}
        <div className="aspect-[16/9] md:aspect-[21/9] w-full border border-border overflow-hidden bg-muted" data-testid="contact-map">
          <iframe
            key={store.mapQuery}
            title={`Evermark ${store.city} atelier map`}
            src={`https://maps.google.com/maps?q=${store.mapQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(1) contrast(1.05)' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <p className="mt-4 overline text-muted-foreground">Showing: {store.city} — {store.address}</p>
      </section>
    </div>
  );
}
